import { useCallback, useRef, useEffect } from 'react';

/**
 * useDebounceFirstLast
 *
 * Debounces async function calls, executing only the last calls within a debounce period.
 * The debounce period resets after each call.The last call is queued and executed after the period if more calls occur.
 *
 * Returns a tuple: [debouncedFn, { cancel, flush }]
 *
 * - The debounced function returns a Promise and propagates errors to the caller.
 * - Use .catch or try/catch to handle errors from the debounced function.
 * - Callback reference stability (useCallback) is critical for correct debounce behavior.
 * - Any queued (pending) calls are lost if the component unmounts before execution.
 *
 * @template Args Arguments for the debounced function
 * @template R Return type of the debounced function (should be a Promise)
 * @param callback The async function to debounce
 * @param delay Debounce period in ms
 * @returns [debouncedFn, { cancel, flush }]
 *
 * @example
 * const [debouncedSearch, { cancel, flush }] = useDebounceFirstLast(async (query) => {
 *   // ...async logic
 * }, 500);
 *
 * // Usage in an event handler:
 * debouncedSearch('input value').catch(err => { ... });
 *
 * // To flush/cancel:
 * flush();
 * cancel();
 */
function useDebounceLast<Args extends unknown[], R>(
  callback: (...args: Args) => Promise<R>,
  delay: number,
): [(...args: Args) => Promise<R>, { cancel: () => void; flush: () => void }] {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastArgsRef = useRef<Args | null>(null);
  const pendingPromiseRef = useRef<{
    resolve: ((value: R | PromiseLike<R>) => void) | null;
    reject: ((reason?: unknown) => void) | null;
  } | null>(null);
  const isFirstCallRef = useRef(true);
  const isUnmountedRef = useRef(false);

  // Stable callback reference
  const stableCallback = useCallback(callback, [callback]);

  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      // Reject any pending promise on unmount
      if (pendingPromiseRef.current?.reject) {
        pendingPromiseRef.current.reject(new Error('Debounced call was cancelled due to unmount.'));
      }
    };
  }, []);

  const debouncedFn = useCallback(
    (...args: Args): Promise<R> => {
      if (isUnmountedRef.current) {
        return Promise.reject(new Error('Component is unmounted.'));
      }

      // If first call, execute immediately
      // if (isFirstCallRef.current) {
      //   isFirstCallRef.current = false;
      //   return stableCallback(...args);
      // }

      // Otherwise, queue the last call
      lastArgsRef.current = args;

      // If a timer is already running, reset it
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // If a previous promise is pending, reject it (propagate error)
      if (pendingPromiseRef.current?.reject) {
        // pendingPromiseRef.current.reject(new Error('Debounced call was superseded by a new call.'));
      }

      // Return a promise that resolves/rejects when the last call is executed
      return new Promise<R>((resolve, reject) => {
        pendingPromiseRef.current = { resolve, reject };
        timerRef.current = setTimeout(async () => {
          timerRef.current = null;
          isFirstCallRef.current = true;
          const callArgs = lastArgsRef.current;
          lastArgsRef.current = null;
          pendingPromiseRef.current = null;
          if (!callArgs) {
            reject(new Error('No arguments for debounced call.'));
            return;
          }
          try {
            const result = await stableCallback(...callArgs);
            resolve(result);
          } catch (err) {
            reject(err);
          }
        }, delay);
      });
    },
    [delay, stableCallback],
  );

  // Cancel any pending debounced call
  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (pendingPromiseRef.current?.reject) {
      pendingPromiseRef.current.reject(new Error('Debounced call was cancelled.'));
      pendingPromiseRef.current = null;
    }
    lastArgsRef.current = null;
    isFirstCallRef.current = true;
  }, []);

  // Immediately execute the last queued call, if any
  const flush = useCallback(() => {
    if (timerRef.current && lastArgsRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      isFirstCallRef.current = true;
      const callArgs = lastArgsRef.current;
      lastArgsRef.current = null;
      const pending = pendingPromiseRef.current;
      pendingPromiseRef.current = null;
      stableCallback(...callArgs)
        .then((result) => {
          pending?.resolve?.(result);
        })
        .catch((err) => {
          pending?.reject?.(err);
        });
    }
  }, [stableCallback]);

  return [debouncedFn, { cancel, flush }];
}

export default useDebounceLast;
